import { zodResolver } from '@hookform/resolvers/zod';
import { Stack } from '@mantine/core';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { IssnListControl } from '@/module/Journal/components/form/IssnListControl';
import { BdiMultiSelectControl } from '@/module/Nomen/Bdi/BdiMultiSelectControl';
import { LanguageSelectControl } from '@/module/Nomen/Language/LanguageSelectControl';
import { PublisherSelectControl } from '@/module/Nomen/Publisher/PublisherSelectControl';

import { Content, Form, FormSubmit, TextInputControl } from '@/core/components';

import { JournalSchema } from './model/JournalSchema';

interface JournalFormProps extends FormSubmit<JournalSchema> {
  title: string;
}

export function JournalForm(props: JournalFormProps) {
  const { title, schema, defaultValues, values, isSubmitting, onCancel, onSubmit } = props;
  const { t } = useTranslation();
  const form = useForm<JournalSchema>({
    defaultValues,
    values,
    resolver: zodResolver(schema),
  });

  return (
    <FormProvider {...form}>
      <Form
        title={title}
        isSubmitting={isSubmitting}
        onCancel={onCancel}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <Stack>
          <Content type="full">
            <TextInputControl<JournalSchema>
              name="title"
              label={t('journals.form.fields.title')}
              isSubmitting={isSubmitting}
            />
            <LanguageSelectControl<JournalSchema>
              label={t('journals.form.fields.language')}
              name="language"
              isSubmitting={isSubmitting}
            />
            <PublisherSelectControl<JournalSchema>
              label={t('journals.form.fields.publisher')}
              name="publisher"
              isSubmitting={isSubmitting}
            />
            <BdiMultiSelectControl<JournalSchema>
              label={t('journals.form.fields.bdiList')}
              name="bdis"
              isSubmitting={isSubmitting}
            />
          </Content>
          <Content type="full">
            <IssnListControl<JournalSchema>
              label={t('journals.form.fields.issn')}
              name="issn"
              isSubmitting={isSubmitting}
            />
          </Content>
        </Stack>
      </Form>
    </FormProvider>
  );
}
